import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FaCheck, FaCreditCard, FaEdit, FaTimes } from "react-icons/fa";
import {
  useGetCooperativePaymentSettingsQuery,
  useUpdateCooperativePaymentSettingsMutation,
} from "../../store/auth/authApi";

function statusBadgeClass(label) {
  if (!label) return "bg-gray-100 text-gray-600";
  if (label.includes("Organisation")) return "bg-[#eaf1f4] text-[#31778c]";
  if (label.includes("Platform")) return "bg-[#ddfcdc] text-[#19bc31]";
  if (label.includes("hidden") || label.includes("Not configured")) {
    return "bg-[#fcefe2] text-[#da6900]";
  }
  if (label === "Disabled") return "bg-gray-100 text-gray-500";
  return "bg-gray-100 text-gray-600";
}

export default function CooperativePaymentMethodsCard({ companyId }) {
  const { data, isLoading, isError, error } =
    useGetCooperativePaymentSettingsQuery(companyId, { skip: !companyId });
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateCooperativePaymentSettingsMutation();

  const [editing, setEditing] = useState(false);
  const [password, setPassword] = useState("");
  const [form, setForm] = useState({
    paystack_enabled: true,
    alatpay_enabled: false,
    society_bank_transfer_enabled: false,
    paystack_public_key: "",
    paystack_secret_key: "",
    paystack_webhook_secret: "",
    alatpay_public_key: "",
    alatpay_business_id: "",
    alatpay_webhook_secret: "",
    clear_paystack_credentials: false,
    clear_alatpay_credentials: false,
  });

  useEffect(() => {
    if (!data || editing) return;
    setForm((prev) => ({
      ...prev,
      paystack_enabled: Boolean(data.paystack_enabled),
      alatpay_enabled: Boolean(data.alatpay_enabled),
      society_bank_transfer_enabled: Boolean(data.society_bank_transfer_enabled),
      alatpay_business_id: data.alatpay?.business_id || "",
      paystack_public_key: "",
      paystack_secret_key: "",
      paystack_webhook_secret: "",
      alatpay_public_key: "",
      alatpay_webhook_secret: "",
      clear_paystack_credentials: false,
      clear_alatpay_credentials: false,
    }));
  }, [data, editing]);

  const handleSave = async () => {
    if (!password.trim()) {
      toast.error("Enter your password to confirm this change.");
      return;
    }
    try {
      const payload = {
        companyId,
        password,
        paystack_enabled: form.paystack_enabled,
        alatpay_enabled: form.alatpay_enabled,
        society_bank_transfer_enabled: form.society_bank_transfer_enabled,
        clear_paystack_credentials: form.clear_paystack_credentials,
        clear_alatpay_credentials: form.clear_alatpay_credentials,
      };
      if (!form.clear_paystack_credentials) {
        if (form.paystack_public_key.trim()) {
          payload.paystack_public_key = form.paystack_public_key.trim();
        }
        if (form.paystack_secret_key.trim()) {
          payload.paystack_secret_key = form.paystack_secret_key.trim();
        }
        if (form.paystack_webhook_secret.trim()) {
          payload.paystack_webhook_secret = form.paystack_webhook_secret.trim();
        }
      }
      if (!form.clear_alatpay_credentials) {
        if (form.alatpay_public_key.trim()) {
          payload.alatpay_public_key = form.alatpay_public_key.trim();
        }
        if (form.alatpay_business_id.trim()) {
          payload.alatpay_business_id = form.alatpay_business_id.trim();
        }
        if (form.alatpay_webhook_secret.trim()) {
          payload.alatpay_webhook_secret = form.alatpay_webhook_secret.trim();
        }
      }

      await updateSettings(payload).unwrap();
      toast.success("Payment methods updated.");
      setPassword("");
      setEditing(false);
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Update failed.");
    }
  };

  if (!companyId) return null;

  return (
    <div className="md:col-span-2 space-y-4 mt-6 pt-6 border-t">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div>
          <h4 className="text-md font-semibold text-gray-700 flex items-center">
            <FaCreditCard className="mr-2 text-[#8B313A]" />
            Payment methods
          </h4>
          <p className="text-xs text-gray-500 mt-1 max-w-xl">
            Enable Paystack, ALATPay, and/or society bank transfer for this
            cooperative. Leave credential fields empty to keep using platform
            keys. Organisation keys override platform fallback.
          </p>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 px-4 py-2 border border-[#8B313A] text-[#8B313A] rounded-lg hover:bg-[#8B313A]/5 transition-colors shrink-0"
          >
            <FaEdit className="text-sm" />
            <span>Edit payment methods</span>
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setPassword("");
              }}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <FaTimes className="text-sm" />
              <span>Cancel</span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <FaCheck className="text-sm" />
              <span>{isSaving ? "Saving…" : "Save methods"}</span>
            </button>
          </div>
        )}
      </div>

      {isError && (
        <p className="text-sm text-red-600">
          {error?.data?.message || "Could not load payment settings."}
        </p>
      )}
      {isLoading && !data && (
        <p className="text-sm text-gray-500">Loading payment settings…</p>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RailCard
            title="Paystack"
            description="Card checkout. Enabled by default; uses platform keys unless organisation keys are saved."
            enabled={form.paystack_enabled}
            statusLabel={data.paystack?.status_label}
            maskedPublic={data.paystack?.public_key_masked}
            secretSet={data.paystack?.secret_key_set}
            editing={editing}
            onToggle={(checked) =>
              setForm((f) => ({ ...f, paystack_enabled: checked }))
            }
          >
            {editing && (
              <div className="space-y-2 mt-3">
                <input
                  type="text"
                  placeholder="Public key (optional)"
                  value={form.paystack_public_key}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, paystack_public_key: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <input
                  type="password"
                  placeholder="Secret key (leave blank to keep)"
                  value={form.paystack_secret_key}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, paystack_secret_key: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  autoComplete="new-password"
                />
                <input
                  type="password"
                  placeholder="Webhook secret (optional)"
                  value={form.paystack_webhook_secret}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      paystack_webhook_secret: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  autoComplete="new-password"
                />
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={form.clear_paystack_credentials}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        clear_paystack_credentials: e.target.checked,
                      }))
                    }
                  />
                  Clear organisation Paystack keys (fall back to platform)
                </label>
              </div>
            )}
          </RailCard>

          <RailCard
            title="ALATPay (Wema)"
            description="Hosted checkout. Hidden from members unless enabled and credentials resolve."
            enabled={form.alatpay_enabled}
            statusLabel={data.alatpay?.status_label}
            maskedPublic={data.alatpay?.public_key_masked}
            secretSet={data.alatpay?.webhook_secret_set}
            extra={`Business ID: ${data.alatpay?.business_id || "not set"}`}
            editing={editing}
            onToggle={(checked) =>
              setForm((f) => ({ ...f, alatpay_enabled: checked }))
            }
          >
            {editing && (
              <div className="space-y-2 mt-3">
                <input
                  type="text"
                  placeholder="Business ID"
                  value={form.alatpay_business_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, alatpay_business_id: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <input
                  type="password"
                  placeholder="Public API key (leave blank to keep)"
                  value={form.alatpay_public_key}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, alatpay_public_key: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  autoComplete="new-password"
                />
                <input
                  type="password"
                  placeholder="Webhook secret (leave blank to keep)"
                  value={form.alatpay_webhook_secret}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      alatpay_webhook_secret: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  autoComplete="new-password"
                />
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={form.clear_alatpay_credentials}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        clear_alatpay_credentials: e.target.checked,
                      }))
                    }
                  />
                  Clear organisation ALATPay keys (fall back to platform)
                </label>
              </div>
            )}
          </RailCard>

          <RailCard
            title="Society bank transfer"
            description="Member pays the cooperative account. Staff confirm. No extra fees. Requires a society bank account in the portal."
            enabled={form.society_bank_transfer_enabled}
            statusLabel={
              data.society_bank_transfer?.available
                ? "Available to members"
                : form.society_bank_transfer_enabled
                ? "Enabled — add a society bank account"
                : "Disabled"
            }
            extra={
              data.society_bank_transfer?.has_company_banks
                ? "Society accounts on file"
                : "No society accounts yet"
            }
            editing={editing}
            onToggle={(checked) =>
              setForm((f) => ({
                ...f,
                society_bank_transfer_enabled: checked,
              }))
            }
          />
        </div>
      )}

      {editing && (
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm with your password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2"
            placeholder="Admin password"
            autoComplete="current-password"
          />
        </div>
      )}
    </div>
  );
}

function RailCard({
  title,
  description,
  enabled,
  statusLabel,
  maskedPublic,
  secretSet,
  extra,
  editing,
  onToggle,
  children,
}) {
  return (
    <div className="rounded-lg border border-[#e0e0e0] bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <h5 className="font-semibold text-gray-800">{title}</h5>
        {editing ? (
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={enabled}
              onChange={(e) => onToggle(e.target.checked)}
            />
            <span className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-[#19bc31] relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full peer-checked:after:translate-x-5 after:transition" />
          </label>
        ) : (
          <span
            className={`text-xs px-2 py-1 rounded-pill ${
              enabled ? "bg-[#ddfcdc] text-[#19bc31]" : "bg-gray-100 text-gray-500"
            }`}
          >
            {enabled ? "On" : "Off"}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-2">{description}</p>
      {statusLabel && (
        <span
          className={`inline-block text-xs px-2 py-1 rounded-pill mt-3 ${statusBadgeClass(
            statusLabel
          )}`}
        >
          {statusLabel}
        </span>
      )}
      {maskedPublic && (
        <p className="text-xs text-gray-500 mt-2">Public key: {maskedPublic}</p>
      )}
      {secretSet !== undefined && (
        <p className="text-xs text-gray-500 mt-1">
          Secret: {secretSet ? "saved" : "not set"}
        </p>
      )}
      {extra && <p className="text-xs text-gray-500 mt-1">{extra}</p>}
      {children}
    </div>
  );
}
